/**
 * Outil « Règle, équerre, compas » (Boîte à outils, 6e → 3e).
 *
 * Construire avec les instruments : points, segments, droites, demi-droites
 * (règle), cercles passant par un point, de rayon donné ou d'écartement
 * reporté (compas),
 * perpendiculaires et parallèles (équerre). La figure est DYNAMIQUE : on
 * déplace un point libre, tout suit. Le programme de construction s'écrit au
 * fur et à mesure et se rejoue pas à pas. Défis : perpendiculaire, médiatrice
 * au compas, triangle de côtés donnés, symétrique, parallélogramme, hauteur,
 * cercle circonscrit, vérifiés sur la figure.
 *
 * Au clavier : un curseur (flèches, au demi-centimètre) et Entrée pour
 * « toucher » ; en mode Déplacer, Entrée prend ou pose le point ; Échap
 * annule l'étape en cours (sans fermer la boîte à outils).
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type SetStateAction,
} from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, SECONDARY_BUTTON, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, INK } from '../../ui/svg-parts';
import {
  CHALLENGES,
  candidates,
  dist,
  distTo,
  evaluate,
  label,
  makeFrame,
  nextName,
  nextPointName,
  refreshNear,
  snapHalf,
  stepText,
  visibleEnds,
  type Challenge,
  type LineStyle,
  type Obj,
  type P,
  type Val,
} from './geom';

type Tool = 'point' | 'segment' | 'line' | 'ray' | 'circle' | 'perp' | 'par' | 'move';

const TOOLS: ReadonlyArray<{ id: Tool; label: string; minRank: number }> = [
  { id: 'point', label: 'Point', minRank: 1 },
  { id: 'segment', label: 'Segment', minRank: 1 },
  { id: 'line', label: 'Droite', minRank: 1 },
  { id: 'ray', label: 'Demi-droite', minRank: 1 },
  { id: 'circle', label: 'Compas', minRank: 1 },
  { id: 'perp', label: 'Équerre (perpendiculaire)', minRank: 1 },
  { id: 'par', label: 'Parallèle', minRank: 1 },
  { id: 'move', label: 'Déplacer', minRank: 1 },
];

const W = 340;
const H = 280;
const F = makeFrame();
/** tolérance au doigt, et léger avantage aux points déjà placés sur les intersections */
const TOL_PX = 22;
const POINT_BIAS_PX = 4;
const USER = 'var(--color-primary-600)';
const GIVEN = INK;
const CANDIDATE = '#d97706';

type Compass = 'through' | 'radius' | 'transfer';

/** consigne de l'étape en cours */
function stepPrompt(tool: Tool | null, pending: readonly number[], compass: Compass): string {
  switch (tool) {
    case null:
      return 'Choisis un instrument.';
    case 'point':
      return 'Touche la feuille pour placer un point, ou un point orange pour placer une intersection.';
    case 'segment':
    case 'line':
    case 'ray':
      return pending.length ? 'Touche le second point.' : 'Touche le premier point.';
    case 'circle':
      if (compass === 'radius') return 'Règle l’écartement du compas, puis touche le centre.';
      if (compass === 'transfer')
        return pending.length === 0
          ? 'Touche une extrémité de la longueur à reporter.'
          : pending.length === 1
            ? 'Touche l’autre extrémité : le compas prend cet écartement.'
            : 'Touche le centre du cercle.';
      return pending.length
        ? 'Touche un point du cercle (l’écartement du compas).'
        : 'Touche le centre du cercle.';
    case 'perp':
    case 'par':
      return pending.length
        ? 'Touche le point par lequel elle passe.'
        : `Touche la droite à laquelle elle sera ${tool === 'perp' ? 'perpendiculaire' : 'parallèle'}.`;
    case 'move':
      return 'Fais glisser un point libre : la figure suit.';
  }
}

function useConstruction(initial: Obj[]) {
  const [objs, setObjsRaw] = useState<Obj[]>(initial);
  const nextId = useRef(1);
  const vals = useMemo(() => evaluate(objs), [objs]);
  const setObjs = (next: SetStateAction<Obj[]>) =>
    setObjsRaw((prev) => {
      const o = typeof next === 'function' ? next(prev) : next;
      return refreshNear(o, evaluate(o));
    });
  const add = (o: Omit<Obj, 'id'>) => {
    const obj = { ...o, id: nextId.current++ } as Obj;
    return obj;
  };
  return { objs, vals, setObjs, add };
}

function Figure({
  objs,
  vals,
  shown,
  pending,
  cands,
  showCands,
  showLengths,
  cursor,
  showCursor,
  localEscape,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onKeyDown,
  onFocus,
  onBlur,
  descId,
}: {
  objs: readonly Obj[];
  vals: ReadonlyArray<Val | null>;
  /** nombre d'objets montrés (rejouer) */
  shown: number;
  pending: readonly number[];
  cands: ReadonlyArray<{ p: P }>;
  showCands: boolean;
  showLengths: boolean;
  cursor: P;
  /** curseur du clavier visible (après usage du clavier, figure active) */
  showCursor: boolean;
  /** une étape est en cours : Échap l'annule ici, sans fermer la boîte à outils */
  localEscape: boolean;
  onPointerDown: (e: PointerEvent<SVGSVGElement>) => void;
  onPointerMove: (e: PointerEvent<SVGSVGElement>) => void;
  onPointerUp: (e: PointerEvent<SVGSVGElement>) => void;
  onKeyDown: (e: KeyboardEvent<SVGSVGElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  descId: string;
}) {
  const items = objs.slice(0, shown).map((o, i) => ({ o, v: vals[i] ?? null }));
  const clipId = useId();
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="application"
      aria-roledescription="feuille de construction"
      aria-label="Feuille de construction"
      aria-describedby={descId}
      data-local-escape={localEscape || undefined}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{ touchAction: 'none' }}
      className={cn(
        'border-ink-100 focus-visible:ring-primary-500 block w-full max-w-[420px] select-none rounded-2xl border outline-none focus-visible:ring-2',
        FIGURE_BG_CLASS,
      )}
    >
      {/* quadrillage d'un centimètre */}
      {Array.from({ length: 11 }, (_, i) => (
        <line
          key={`gx${i}`}
          x1={F.sx(i)}
          x2={F.sx(i)}
          y1={F.sy(0)}
          y2={F.sy(8)}
          stroke="var(--color-ink-100)"
        />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <line
          key={`gy${i}`}
          x1={F.sx(0)}
          x2={F.sx(10)}
          y1={F.sy(i)}
          y2={F.sy(i)}
          stroke="var(--color-ink-100)"
        />
      ))}
      <defs>
        <clipPath id={clipId}>
          <rect x={F.sx(0)} y={F.sy(8)} width={10 * F.scale} height={8 * F.scale} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {items.map(({ o, v }) => {
          if (!v || v.kind === 'point') return null;
          const color = o.given ? GIVEN : USER;
          const hot = pending.includes(o.id);
          if (v.kind === 'circle')
            return (
              <circle
                key={o.id}
                cx={F.sx(v.c.c.x)}
                cy={F.sy(v.c.c.y)}
                r={v.c.r * F.scale}
                fill="none"
                stroke={color}
                strokeWidth={hot ? 3 : 1.6}
              />
            );
          const ends = visibleEnds(v.l, F);
          if (!ends) return null;
          return (
            <line
              key={o.id}
              x1={F.sx(ends[0].x)}
              y1={F.sy(ends[0].y)}
              x2={F.sx(ends[1].x)}
              y2={F.sy(ends[1].y)}
              stroke={color}
              strokeWidth={hot ? 3.4 : o.given ? 1.8 : 1.6}
              strokeLinecap="round"
            />
          );
        })}
      </g>
      {showLengths &&
        items.map(({ o, v }) => {
          if (!v || v.kind !== 'line' || v.l.style !== 'segment') return null;
          const m = { x: (v.l.a.x + v.l.b.x) / 2, y: (v.l.a.y + v.l.b.y) / 2 };
          return (
            <HaloText
              key={`len${o.id}`}
              x={F.sx(m.x)}
              y={F.sy(m.y) - 6}
              fontSize={12}
              textAnchor="middle"
              fill={INK}
            >
              {`${formatNumber(dist(v.l.a, v.l.b), 2)} cm`}
            </HaloText>
          );
        })}
      {showCands &&
        cands.map((c, i) => (
          <circle
            key={`c${i}`}
            cx={F.sx(c.p.x)}
            cy={F.sy(c.p.y)}
            r={5}
            fill="none"
            stroke={CANDIDATE}
            strokeWidth={2}
          />
        ))}
      {items.map(({ o, v }) => {
        if (!v || v.kind !== 'point') return null;
        const x = F.sx(v.p.x);
        const y = F.sy(v.p.y);
        const hot = pending.includes(o.id);
        const color = o.given ? GIVEN : USER;
        // étiquette gardée dans la feuille
        const lx = Math.min(x + 7, W - 22);
        const ly = Math.max(y - 7, 14);
        return (
          <g key={o.id}>
            {hot && <circle cx={x} cy={y} r={9} fill="none" stroke={USER} strokeWidth={2} />}
            {o.def.t === 'free' ? (
              // point libre (déplaçable) : un disque
              <circle
                cx={x}
                cy={y}
                r={4}
                fill={color}
                stroke="var(--fig-bg, #fff)"
                strokeWidth={1.2}
              />
            ) : (
              // point construit : une croix, comme sur le cahier
              <path
                d={`M${x - 4} ${y - 4}L${x + 4} ${y + 4}M${x - 4} ${y + 4}L${x + 4} ${y - 4}`}
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
              />
            )}
            <HaloText x={lx} y={ly} fontSize={13} fontWeight={700} fill={INK}>
              {o.name}
            </HaloText>
          </g>
        );
      })}
      {showCursor && (
        <g stroke="var(--color-primary-500)" strokeWidth={1.4} aria-hidden>
          <line
            x1={F.sx(cursor.x) - 9}
            x2={F.sx(cursor.x) + 9}
            y1={F.sy(cursor.y)}
            y2={F.sy(cursor.y)}
          />
          <line
            x1={F.sx(cursor.x)}
            x2={F.sx(cursor.x)}
            y1={F.sy(cursor.y) - 9}
            y2={F.sy(cursor.y) + 9}
          />
        </g>
      )}
    </svg>
  );
}

function Builder({
  rank,
  challenge,
  onCheck,
}: {
  rank: number;
  /** défi en cours (null : construction libre) */
  challenge: Challenge | null;
  onCheck?: (ok: boolean) => void;
}) {
  const given = useMemo(
    () => (challenge ? challenge.given.map((o) => ({ ...o })) : []),
    [challenge],
  );
  const { objs, vals, setObjs, add } = useConstruction(given);
  // un défi ne souffle pas l'instrument
  const [tool, setTool] = useState<Tool | null>(challenge ? null : 'segment');
  const [pending, setPending] = useState<number[]>([]);
  /** figure au début de l'étape en cours (Annuler / Échap y reviennent) */
  const stepBase = useRef<Obj[] | null>(null);
  const [compass, setCompass] = useState<Compass>('through');
  const [radius, setRadius] = useState(3);
  const [lengths, setLengths] = useState(false);
  const [replay, setReplay] = useState<number | null>(null);
  const [cursor, setCursor] = useState<P>({ x: 5, y: 4 });
  const [focused, setFocused] = useState(false);
  const [keyboard, setKeyboard] = useState(false);
  const [grab, setGrab] = useState<number | null>(null);
  const [say, setSay] = useState('');
  const descId = useId();

  const cands = useMemo(() => candidates(objs, vals), [objs, vals]);
  const ok = challenge ? challenge.check(objs, vals) : false;
  useEffect(() => onCheck?.(ok), [ok, onCheck]);

  // rejouer : un objet de plus toutes les 700 ms
  useEffect(() => {
    if (replay === null) return;
    if (replay >= objs.length) {
      const t = setTimeout(() => setReplay(null), 900);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setReplay(replay + 1), 700);
    return () => clearTimeout(t);
  }, [replay, objs.length]);

  const tolCm = TOL_PX / F.scale;
  const nearestPoint = (w: P): { id: number; d: number } | null => {
    let best: { id: number; d: number } | null = null;
    objs.forEach((o, i) => {
      const v = vals[i];
      if (o.kind !== 'point' || !v || v.kind !== 'point') return;
      const d = dist(v.p, w);
      if (d < tolCm && (!best || d < best.d)) best = { id: o.id, d };
    });
    return best;
  };
  /** nom du prochain point : celui que le défi attend, sinon la lettre suivante */
  const pointName = (list: readonly Obj[]) => {
    const used = new Set(list.filter((o) => o.kind === 'point').map((o) => o.name));
    return nextPointName(
      list,
      challenge?.names?.find((n) => !used.has(n)),
    );
  };
  /** point touché : existant, intersection à créer, ou nouveau point libre */
  const resolvePoint = (w: P, list: Obj[]): { id: number; list: Obj[] } => {
    const hit = nearestPoint(w);
    const c = cands
      .map((x) => ({ x, d: dist(x.p, w) }))
      .filter((x) => x.d < tolCm)
      .sort((a, b) => a.d - b.d)[0];
    // le plus proche des deux, avec un léger avantage au point déjà placé
    if (hit && (!c || hit.d <= c.d + POINT_BIAS_PX / F.scale)) return { id: hit.id, list };
    const obj = c
      ? add({
          kind: 'point',
          name: pointName(list),
          def: { t: 'inter', a: c.x.a, b: c.x.b, near: c.x.p },
        })
      : add({
          kind: 'point',
          name: pointName(list),
          def: {
            t: 'free',
            x: Math.max(0, Math.min(10, snapHalf(w.x))),
            y: Math.max(0, Math.min(8, snapHalf(w.y))),
          },
        });
    return { id: obj.id, list: [...list, obj] };
  };
  const nearestCurve = (w: P, linesOnly: boolean): number | null => {
    let best: { id: number; d: number } | null = null;
    objs.forEach((o, i) => {
      const v = vals[i];
      if (!v || v.kind === 'point' || (linesOnly && v.kind !== 'line')) return;
      const d = distTo(v, w);
      if (d < tolCm && (!best || d < best.d)) best = { id: o.id, d };
    });
    return best ? (best as { id: number }).id : null;
  };

  const describe = (list: readonly Obj[], id: number) => {
    const o = list.find((x) => x.id === id);
    return o ? label(list, o) : '';
  };

  /** l'étape continue : on retient la figure de départ pour pouvoir l'annuler */
  const continueStep = (next: number[], list: Obj[]) => {
    if (pending.length === 0) stepBase.current = objs;
    setPending(next);
    setObjs(list);
  };
  const finishStep = (list: Obj[], o: Obj) => {
    setObjs(list);
    setPending([]);
    stepBase.current = null;
    setSay(stepText(list, o));
  };
  const cancelStep = () => {
    if (stepBase.current) setObjs(stepBase.current);
    stepBase.current = null;
    setPending([]);
    setGrab(null);
  };

  const tap = (w: P) => {
    if (replay !== null) return;
    if (tool === null) {
      setSay('Choisis d’abord un instrument.');
      return;
    }
    let list = objs;
    if (tool === 'move') {
      // clavier : prendre / poser un point libre
      if (grab !== null) {
        setGrab(null);
        setSay('Point posé.');
        return;
      }
      const hit = nearestPoint(w);
      const o = objs.find((x) => x.id === hit?.id);
      if (o && o.kind === 'point' && o.def.t === 'free' && !o.given) {
        setGrab(o.id);
        setSay(`Point ${o.name} pris : flèches pour le déplacer, Entrée pour le poser.`);
      } else
        setSay(
          'Aucun point libre ici (les points donnés et les intersections ne se déplacent pas).',
        );
      return;
    }
    if (tool === 'point') {
      const r = resolvePoint(w, list);
      if (r.list !== list) finishStep(r.list, r.list.at(-1)!);
      return;
    }
    if (tool === 'segment' || tool === 'line' || tool === 'ray') {
      const r = resolvePoint(w, list);
      list = r.list;
      if (pending.length === 0) {
        continueStep([r.id], list);
        setSay(`Premier point : ${describe(list, r.id)}.`);
        return;
      }
      if (r.id === pending[0]) return;
      const line = add({
        kind: 'line',
        name: '',
        def: { t: '2p', p: pending[0]!, q: r.id, style: tool as LineStyle },
      });
      finishStep([...list, line], line);
      return;
    }
    if (tool === 'circle') {
      const r = resolvePoint(w, list);
      list = r.list;
      if (compass === 'radius') {
        const c = add({
          kind: 'circle',
          name: nextName(list, 'circle'),
          def: { t: 'cr', c: r.id, r: radius },
        });
        finishStep([...list, c], c);
        return;
      }
      if (compass === 'transfer') {
        if (pending.length < 2) {
          if (pending[0] === r.id) return;
          continueStep([...pending, r.id], list);
          setSay(
            pending.length === 0
              ? `Première extrémité : ${describe(list, r.id)}.`
              : `Écartement pris : ${describe(list, pending[0]!)}${describe(list, r.id)}.`,
          );
          return;
        }
        const c = add({
          kind: 'circle',
          name: nextName(list, 'circle'),
          def: { t: 'cd', c: r.id, p: pending[0]!, q: pending[1]! },
        });
        finishStep([...list, c], c);
        return;
      }
      if (pending.length === 0) {
        continueStep([r.id], list);
        setSay(`Centre : ${describe(list, r.id)}.`);
        return;
      }
      if (r.id === pending[0]) return;
      const c = add({
        kind: 'circle',
        name: nextName(list, 'circle'),
        def: { t: 'cp', c: pending[0]!, p: r.id },
      });
      finishStep([...list, c], c);
      return;
    }
    // équerre, parallèle
    if (pending.length === 0) {
      const id = nearestCurve(w, true);
      if (id === null) {
        setSay('Touche d’abord une droite (ou un segment).');
        return;
      }
      continueStep([id], list);
      setSay(`Droite choisie : ${describe(list, id)}.`);
      return;
    }
    const r = resolvePoint(w, list);
    list = r.list;
    const l = add({
      kind: 'line',
      name: nextName(list, 'line'),
      def: { t: tool === 'perp' ? 'perp' : 'par', line: pending[0]!, p: r.id },
    });
    finishStep([...list, l], l);
  };

  // glisser un point libre (en gardant l'écart entre le doigt et le point)
  const drag = useRef<{ id: number; dx: number; dy: number } | null>(null);
  const worldOf = (e: PointerEvent<SVGSVGElement>): P | null => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return null;
    return {
      x: F.wx(((e.clientX - r.left) / r.width) * W),
      y: F.wy(((e.clientY - r.top) / r.height) * H),
    };
  };
  const moveFree = (id: number, w: P) => {
    const x = Math.max(0, Math.min(10, Math.round(w.x * 10) / 10));
    const y = Math.max(0, Math.min(8, Math.round(w.y * 10) / 10));
    setObjs((prev) =>
      prev.map((o) => (o.id === id && o.kind === 'point' ? { ...o, def: { t: 'free', x, y } } : o)),
    );
  };
  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    // un seul doigt, bouton principal
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const w = worldOf(e);
    if (!w) return;
    setKeyboard(false);
    setCursor({
      x: Math.max(0, Math.min(10, snapHalf(w.x))),
      y: Math.max(0, Math.min(8, snapHalf(w.y))),
    });
    if (tool === 'move') {
      if (replay !== null) return;
      const hit = nearestPoint(w);
      const i = objs.findIndex((x) => x.id === hit?.id);
      const o = objs[i];
      const v = vals[i];
      if (o && o.kind === 'point' && o.def.t === 'free' && !o.given && v?.kind === 'point') {
        drag.current = { id: o.id, dx: v.p.x - w.x, dy: v.p.y - w.y };
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
      return;
    }
    tap(w);
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d || !e.isPrimary) return;
    const w = worldOf(e);
    if (w) moveFree(d.id, { x: w.x + d.dx, y: w.y + d.dy });
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const underCursor = (c: P) => {
    const p = nearestPoint(c);
    if (p !== null) return ` : point ${objs.find((o) => o.id === p.id)!.name}`;
    const k = cands.find((x) => dist(x.p, c) < tolCm);
    if (k) return ' : une intersection';
    const l = nearestCurve(c, false);
    if (l !== null) return ` : ${describe(objs, l)}`;
    return '';
  };
  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    const step = 0.5;
    const d = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    }[e.key] as [number, number] | undefined;
    if (d) {
      e.preventDefault();
      setKeyboard(true);
      const c = {
        x: Math.max(0, Math.min(10, cursor.x + d[0])),
        y: Math.max(0, Math.min(8, cursor.y + d[1])),
      };
      setCursor(c);
      if (grab !== null && replay === null) moveFree(grab, c);
      setSay(`Curseur en (${formatNumber(c.x, 1)} ; ${formatNumber(c.y, 1)})${underCursor(c)}.`);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setKeyboard(true);
      tap(cursor);
    } else if (e.key === 'Escape' && (pending.length > 0 || grab !== null)) {
      e.preventDefault();
      cancelStep();
      setSay('Étape annulée.');
    }
  };

  const undo = () => {
    if (pending.length > 0) {
      cancelStep();
      setSay('Étape en cours annulée.');
      return;
    }
    const last = objs.at(-1);
    if (!last || last.given) return;
    setObjs(objs.slice(0, -1));
    setSay('Dernière étape annulée.');
  };
  const reset = () => {
    setObjs(given.map((o) => ({ ...o })));
    setPending([]);
    stepBase.current = null;
    setGrab(null);
    setSay('');
  };

  const userSteps = objs.filter((o) => !o.given);
  const shown = replay ?? objs.length;
  const givenCount = objs.filter((o) => o.given).length;
  const needsPoint =
    tool !== null &&
    tool !== 'move' &&
    !((tool === 'perp' || tool === 'par') && pending.length === 0);
  const replaying = replay !== null;

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Instrument" className="flex flex-wrap gap-1.5">
        {TOOLS.filter((t) => t.minRank <= rank).map((t) => (
          <ToggleChip
            key={t.id}
            on={tool === t.id}
            onClick={() => {
              setTool(t.id);
              setPending([]);
              stepBase.current = null;
              setGrab(null);
            }}
          >
            {t.label}
          </ToggleChip>
        ))}
      </div>
      {tool === 'circle' && (
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            ariaLabel="Compas"
            value={compass}
            options={[
              { value: 'through', label: 'Passant par un point' },
              { value: 'radius', label: 'Rayon donné' },
              { value: 'transfer', label: 'Reporter une longueur' },
            ]}
            onChange={(v) => {
              setCompass(v);
              setPending([]);
              stepBase.current = null;
            }}
          />
          {compass === 'radius' && (
            <div className="w-40">
              <Stepper
                label="Rayon"
                value={radius}
                min={0.5}
                max={8}
                step={0.5}
                format={(v) => `${formatNumber(v, 1)} cm`}
                onChange={setRadius}
              />
            </div>
          )}
        </div>
      )}
      <p className="text-ink-700 min-h-10 text-sm">
        {frenchSpaces(stepPrompt(tool, pending, compass))}
      </p>
      <Figure
        objs={objs}
        vals={vals}
        shown={shown}
        pending={pending}
        cands={cands}
        showCands={needsPoint && !replaying}
        showLengths={lengths}
        cursor={cursor}
        showCursor={focused && keyboard}
        localEscape={pending.length > 0 || grab !== null}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        descId={descId}
      />
      <p id={descId} className="sr-only">
        Au clavier : les flèches déplacent un curseur d’un demi-centimètre, Entrée touche la feuille
        à cet endroit, Échap annule l’étape en cours.
      </p>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(say)}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={undo}
          disabled={replaying || (objs.length <= givenCount && pending.length === 0)}
        >
          Annuler
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={reset} disabled={replaying}>
          Tout effacer
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => {
            cancelStep();
            setReplay(givenCount);
          }}
          disabled={userSteps.length === 0 || replaying}
        >
          Rejouer
        </button>
        <ToggleChip on={lengths} onClick={() => setLengths(!lengths)}>
          Longueurs
        </ToggleChip>
      </div>
      {challenge && (
        <div aria-live="polite">
          {ok && (
            <Note live={false} tone="success">
              {frenchSpaces(`Bravo : ${challenge.title.toLowerCase()} réussi !`)}
            </Note>
          )}
        </div>
      )}
      <div className="bg-ink-50 rounded-2xl px-3 py-2">
        <p className="text-ink-700 text-sm font-semibold">Programme de construction</p>
        {userSteps.length === 0 ? (
          <p className="text-ink-600 mt-1 text-sm">Chaque étape s’écrira ici.</p>
        ) : (
          <ol
            aria-label="Programme de construction"
            className="mt-1 list-decimal space-y-0.5 pl-5 text-sm"
          >
            {userSteps.map((o) => {
              const idx = objs.indexOf(o);
              return (
                <li
                  key={o.id}
                  className={cn(
                    'text-ink-800',
                    replaying && idx === replay - 1 && 'text-primary-600 font-semibold',
                    replaying && idx >= replay && 'opacity-40',
                    vals[idx] === null && 'line-through',
                  )}
                >
                  {frenchSpaces(stepText(objs, o))}
                </li>
              );
            })}
          </ol>
        )}
        {vals.some((v, i) => v === null && !objs[i]!.given) && (
          <p className="text-ink-600 mt-1 text-xs">
            Une étape barrée n’existe plus avec cette figure (deux lignes qui ne se coupent plus).
          </p>
        )}
      </div>
    </div>
  );
}

function ChallengeMode({ rank }: { rank: number }) {
  const list = CHALLENGES.filter((c) => c.minRank <= rank);
  const [id, setId] = useState(list[0]!.id);
  const [hint, setHint] = useState(false);
  const [done, setDone] = useState<string[]>([]);
  const ch = list.find((c) => c.id === id)!;
  return (
    <div className="space-y-3">
      <div role="group" aria-label="Défi" className="flex flex-wrap gap-1.5">
        {list.map((c) => (
          <ToggleChip
            key={c.id}
            on={c.id === id}
            onClick={() => {
              setId(c.id);
              setHint(false);
            }}
          >
            {`${done.includes(c.id) ? '✓ ' : ''}${c.title}`}
          </ToggleChip>
        ))}
      </div>
      <p className="text-ink-900 text-base font-semibold">{frenchSpaces(ch.statement)}</p>
      {hint ? (
        <Note live>{frenchSpaces(ch.hint)}</Note>
      ) : (
        <button type="button" className={SECONDARY_BUTTON} onClick={() => setHint(true)}>
          Un indice
        </button>
      )}
      <Builder
        key={ch.id}
        rank={rank}
        challenge={ch}
        onCheck={(ok) => {
          if (ok && !done.includes(ch.id)) setDone([...done, ch.id]);
        }}
      />
    </div>
  );
}

export function RulerCompassTool({ level }: { level?: string | null }) {
  const rank = Math.min(4, Math.max(1, schoolLevelRank(level) ?? 2));
  const [mode, setMode] = useState<'free' | 'challenge'>('free');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'free', label: 'Construire' },
          { value: 'challenge', label: 'Défis' },
        ]}
        onChange={setMode}
      />
      {mode === 'free' ? <Builder rank={rank} challenge={null} /> : <ChallengeMode rank={rank} />}
    </div>
  );
}
